// Read-only, mobile-friendly playoff bracket for the public share link. Mirrors
// the admin column layout (via the shared `buildBracketColumns`) but renders
// static, glowing node cards. Horizontally scrollable so a wide bracket stays
// usable on a phone. Styling follows the "sleek dark + glow" direction.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import type { BracketNode, TournamentState } from '../../domain';
import { finalNodeId, summarizeFootballMatch, summarizeMatch } from '../../domain';
import { describeRef, playerName } from '../labels';
import { buildBracketColumns } from '../bracketLayout';
import { useTranslation } from '../../state/I18nContext';
import { ACCENT_GRADIENT, glowCard } from './publicStyles';

interface PublicBracketProps {
  tournament: TournamentState;
}

export function PublicBracket({ tournament }: PublicBracketProps) {
  const { t } = useTranslation();
  const columns = useMemo(
    () => buildBracketColumns(tournament.nodeOrder),
    [tournament.nodeOrder],
  );

  const champion = useMemo(() => {
    const finalId = finalNodeId(tournament);
    const winnerId = finalId ? tournament.nodes[finalId]?.winner : null;
    return winnerId ? playerName(tournament, winnerId) : null;
  }, [tournament]);

  if (columns.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('bracket.empty')}
      </Typography>
    );
  }

  return (
    <Box>
      {champion && <ChampionBanner name={champion} label={t('bracket.champion')} />}
      <BracketScroller columnCount={columns.length}>
        {columns.map((column) => (
          <Box key={column.round} sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <Typography
              variant="overline"
              align="center"
              sx={{ mb: 1, color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700 }}
            >
              {t(column.titleKey, column.titleParams)}
            </Typography>
            <Stack spacing={2} sx={{ justifyContent: 'space-around', flexGrow: 1 }}>
              {column.ids.map((id) => (
                <PublicBracketNode key={id} tournament={tournament} node={tournament.nodes[id]} t={t} />
              ))}
            </Stack>
          </Box>
        ))}
      </BracketScroller>
    </Box>
  );
}

/**
 * Horizontal scroller for the round columns with edge-fade cues. On a phone a
 * multi-round bracket is wider than the screen, so we hint that there is more to
 * swipe: a soft gradient fades in on whichever side still has hidden columns.
 * Purely decorative (pointer-events: none) and driven by the live scroll offset,
 * so the cue disappears once you reach an edge.
 */
function BracketScroller({
  children,
  columnCount,
}: {
  children: ReactNode;
  columnCount: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure, columnCount]);

  const fade = (side: 'left' | 'right') => ({
    position: 'absolute' as const,
    top: 0,
    bottom: 0,
    [side]: 0,
    width: 40,
    pointerEvents: 'none' as const,
    opacity: edges[side] ? 1 : 0,
    transition: 'opacity .2s ease',
    zIndex: 1,
    backgroundImage: (theme: Theme) =>
      `linear-gradient(${side === 'left' ? 90 : 270}deg, ${theme.palette.background.default}, ${theme.palette.background.default}00)`,
  });

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          gap: 2.5,
          overflowX: 'auto',
          pb: 1.5,
          pt: 0.5,
          alignItems: 'stretch',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </Box>
      <Box sx={fade('left')} />
      <Box sx={fade('right')} />
    </Box>
  );
}

function ChampionBanner({ name, label }: { name: string; label: string }) {
  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: '1px solid rgba(255,215,0,0.5)',
        backgroundImage:
          'linear-gradient(135deg, rgba(255,215,0,0.18), rgba(255,215,0,0.02))',
        boxShadow: '0 14px 36px -16px rgba(255,215,0,0.5)',
      }}
    >
      <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 30 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="overline" sx={{ color: 'warning.main', letterSpacing: 1.2, lineHeight: 1.2, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="h6" noWrap sx={{ fontWeight: 800 }}>
          {name}
        </Typography>
      </Box>
    </Box>
  );
}

const NODE_WIDTH = 208;

function PublicBracketNode({
  tournament,
  node,
  t,
}: {
  tournament: TournamentState;
  node: BracketNode;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  if (!node) return null;
  const done = node.status === 'COMPLETED';
  const isFootball = tournament.sportId === 'FOOTBALL';
  const summary = summarizeMatch(node.games, node.bestOf);
  const football = summarizeFootballMatch(node.games);
  // Football shows goals — with the shoot-out conversions in brackets after a
  // draw; table tennis shows games won in the best-of series.
  const scoreA = isFootball
    ? football.hasShootout
      ? t('admin.football.scoreWithPens', { goals: football.goalsA, pens: football.shootoutA })
      : football.goalsA
    : summary.gamesA;
  const scoreB = isFootball
    ? football.hasShootout
      ? t('admin.football.scoreWithPens', { goals: football.goalsB, pens: football.shootoutB })
      : football.goalsB
    : summary.gamesB;

  return (
    <Paper elevation={0} sx={[{ width: NODE_WIDTH, overflow: 'hidden' }, glowCard(done)]}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          px: 1.25,
          py: 0.4,
          bgcolor: 'rgba(255,255,255,0.04)',
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: 0.4,
        }}
      >
        {node.label}
      </Typography>
      <ParticipantRow
        name={node.playerA ? playerName(tournament, node.playerA) : describeRef(node.a, tournament, t)}
        resolved={node.playerA !== null}
        score={done ? scoreA : null}
        winner={done && node.winner === node.playerA}
      />
      <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.06)' }} />
      <ParticipantRow
        name={node.playerB ? playerName(tournament, node.playerB) : describeRef(node.b, tournament, t)}
        resolved={node.playerB !== null}
        score={done ? scoreB : null}
        winner={done && node.winner === node.playerB}
      />
    </Paper>
  );
}

function ParticipantRow({
  name,
  resolved,
  score,
  winner,
}: {
  name: string;
  resolved: boolean;
  score: number | string | null;
  winner: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.9,
        position: 'relative',
        backgroundImage: winner
          ? 'linear-gradient(90deg, rgba(244,63,94,0.16), rgba(244,63,94,0))'
          : 'none',
        '&::before': winner
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              backgroundImage: ACCENT_GRADIENT,
            }
          : undefined,
      }}
    >
      {winner && <EmojiEventsIcon sx={{ fontSize: 15, color: 'secondary.main' }} />}
      <Typography
        variant="body2"
        noWrap
        sx={{
          flexGrow: 1,
          fontWeight: winner ? 700 : 400,
          fontStyle: resolved ? 'normal' : 'italic',
          color: resolved ? 'text.primary' : 'text.secondary',
        }}
      >
        {name}
      </Typography>
      {score !== null && (
        <Typography variant="body2" sx={{ fontWeight: 800, color: winner ? 'primary.main' : 'text.secondary' }}>
          {score}
        </Typography>
      )}
    </Box>
  );
}
